import { useEffect } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { api } from '../lib/api';
import { getDeviceId } from '../lib/kv';
import { outbox } from '../outbox';
import { syncMaster } from './master';

const HEARTBEAT_MS = 60_000;
const MASTER_REFRESH_MS = 10 * 60_000;

/**
 * Background sync while logged in (Requirements §9.3–9.4):
 *  - send pending entries when the network comes back, when the app returns to the foreground,
 *    and right after login / app start (the outbox also retries with backoff on its own)
 *  - heartbeat with the number of pending entries, so supervisors see devices still to sync
 *  - refresh the master cache periodically
 */
export function SyncManager() {
  const { status, can } = useAuth();
  const active = status === 'authenticated' && can('count.scan');

  useEffect(() => {
    if (!active) return;
    let stopped = false;

    const flush = () => {
      if (navigator.onLine) void outbox.flush();
    };
    const heartbeat = async () => {
      if (!navigator.onLine || stopped) return;
      try {
        await api.post('/devices/heartbeat', {
          device_id: await getDeviceId(),
          pending_count: outbox.pendingCount(),
          pending_session_ids: outbox.pendingSessionIds(),
        });
      } catch {
        /* offline or server unreachable: next tick */
      }
    };
    const refreshMaster = () => {
      if (navigator.onLine) void syncMaster().catch(() => undefined);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        flush();
        void heartbeat();
      }
    };
    const onOnline = () => {
      flush();
      void heartbeat();
      refreshMaster();
    };

    void outbox.init().then(() => {
      flush();
      void heartbeat();
    });
    refreshMaster();
    const hb = setInterval(() => void heartbeat(), HEARTBEAT_MS);
    const mr = setInterval(refreshMaster, MASTER_REFRESH_MS);
    // report the new pending count soon after it changes (debounced)
    let t: ReturnType<typeof setTimeout> | null = null;
    const off = outbox.subscribe(() => {
      if (t) clearTimeout(t);
      t = setTimeout(() => void heartbeat(), 3000);
    });
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearInterval(hb);
      clearInterval(mr);
      if (t) clearTimeout(t);
      off();
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [active]);

  return null;
}
